// src/components/doctorsJournal/JournalEntryModal.js
import React, { useState, useEffect, useRef } from 'react';
import { X, Save, FileText, Plus, Loader2 } from 'lucide-react';

export const JournalEntryModal = ({
  isOpen,
  onClose,
  onSave,
  entry = null,
  customers = [],
  prefilledCustomerId = null,
  searchCustomers,
  allEntries = [],
  viewMode = false,
  onActivity,
  onAutoSave,
}) => {
  const [customerId, setCustomerId] = useState(prefilledCustomerId || '');
  const [formData, setFormData] = useState({
    session_date: new Date().toISOString().split('T')[0],
    duration_minutes: 50,
    mood_assessment: "neutral",
    session_notes: "",
    homework_assigned: ""
  });
  const [interventions, setInterventions] = useState([]);
  const [newIntervention, setNewIntervention] = useState("");
  const [sessionNumber, setSessionNumber] = useState(1); const [saving, setSaving] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [internalEntryId, setInternalEntryId] = useState(entry?.id || null);
  const internalEntryIdRef = useRef(entry?.id || null);
  const [localViewMode, setLocalViewMode] = useState(viewMode);
  const [autoSaveNotification, setAutoSaveNotification] = useState(false);
  const autoSaveIntervalRef = useRef(null);
  const notificationTimerRef = useRef(null);
  const autoSaveInProgressRef = useRef(false);
  const performAutoSaveRef = useRef(null);

  const customerDropdownRef = useRef(null);
  const customerSearchRef = useRef(null);

  // Load session number when customer selected
  useEffect(() => {
    if (customerId && isOpen) {
      loadSessionNumber();
    }
  }, [customerId, isOpen]);

  const loadSessionNumber = async () => {
    if (entry?.session_number) {
      setSessionNumber(entry.session_number);
    } else {
      const customerEntries = allEntries?.filter(e => e.customer_id === customerId) || [];
      const maxSession = Math.max(0, ...customerEntries.map(e => e.session_number || 0));
      setSessionNumber(maxSession + 1);
    }
  };

  const updateEntryId = (id) => {
    internalEntryIdRef.current = id;
    setInternalEntryId(id);
  };

  // Reset internalEntryId and localViewMode when entry prop changes (new vs existing)
  useEffect(() => {
    updateEntryId(entry?.id || null);
    setLocalViewMode(viewMode);
  }, [entry?.id, isOpen, viewMode]);

  // Always keep performAutoSaveRef fresh so the interval closure is never stale
  useEffect(() => {
    performAutoSaveRef.current = async () => {
      if (autoSaveInProgressRef.current || localViewMode || !onAutoSave) return;
      if (!customerId || !formData.session_notes?.trim()) return;

      autoSaveInProgressRef.current = true;
      try {
        const savedId = await onAutoSave({
          id: internalEntryIdRef.current,
          customer_id: customerId,
          session_number: sessionNumber,
          entry_date: formData.session_date,
          sessionData: { ...formData, interventions_used: interventions },
        });
        if (savedId) updateEntryId(savedId);
        setAutoSaveNotification(true);
        clearTimeout(notificationTimerRef.current);
        notificationTimerRef.current = setTimeout(() => setAutoSaveNotification(false), 3000);
      } catch (e) {
        console.error('Auto-save failed:', e);
      } finally {
        autoSaveInProgressRef.current = false;
      }
    };
  });

  // Clear auto-save interval when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
      clearTimeout(notificationTimerRef.current);
      setAutoSaveNotification(false);
      internalEntryIdRef.current = null;
      autoSaveInProgressRef.current = false;
    }
  }, [isOpen]);

  // Called on every user input: resets the lock timer and starts auto-save interval if not already running
  const handleActivity = () => {
    if (localViewMode) return;
    onActivity?.();
    if (!autoSaveIntervalRef.current) {
      autoSaveIntervalRef.current = setInterval(() => {
        performAutoSaveRef.current?.();
      }, 30 * 1000);
    }
  };

  const addIntervention = () => {
    if (newIntervention.trim()) {
      setInterventions([...interventions, newIntervention.trim()]);
      setNewIntervention("");
    }
  };

  const removeIntervention = (index) => {
    setInterventions(interventions.filter((_, i) => i !== index));
  };

  // Filter customers based on search
  useEffect(() => {
    let isMounted = true;

    const fetchFilteredCustomers = async () => {
      if (!customerSearchTerm.trim()) {
        setFilteredCustomers(customers.slice(0, 10));
        return;
      }

      try {
        const result = searchCustomers ?
          await searchCustomers(customerSearchTerm) :
          customers.filter(c =>
            c.full_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
            c.phone?.includes(customerSearchTerm) ||
            c.email?.toLowerCase().includes(customerSearchTerm.toLowerCase())
          );

        if (isMounted) {
          setFilteredCustomers(result.slice(0, 10));
        }
      } catch (err) {
        console.error('Failed to search customers:', err);
        if (isMounted) setFilteredCustomers([]);
      }
    };

    fetchFilteredCustomers();

    return () => {
      isMounted = false;
    };
  }, [customerSearchTerm, customers, searchCustomers]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (entry) {
      setCustomerId(entry.customer_id || '');

      // Parse decrypted session data
      const sessionData = entry.sessionData || {};
      setFormData({
        session_date: entry.entry_date.split('T')[0] || new Date().toISOString().split('T')[0],
        duration_minutes: sessionData.duration_minutes || 50,
        mood_assessment: sessionData.mood_assessment || "neutral",
        session_notes: sessionData.session_notes || "",
        homework_assigned: sessionData.homework_assigned || ""
      });
      setInterventions(sessionData.interventions_used || []);
      setSessionNumber(entry.session_number || 1);

      if (entry.customer) {
        setSelectedCustomerName(entry.customer.full_name || '');
        setCustomerSearchTerm(entry.customer.full_name || '');
      }
    } else {
      setCustomerId(prefilledCustomerId || '');

      if (prefilledCustomerId) {
        const customer = customers.find(c => c.id === prefilledCustomerId);
        if (customer) {
          setSelectedCustomerName(customer.full_name || '');
          setCustomerSearchTerm(customer.full_name || '');
        }
      } else {
        setCustomerSearchTerm('');
        setSelectedCustomerName('');
      }
    }
  }, [entry, isOpen, prefilledCustomerId, customers]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (autoSaveInProgressRef.current) return;
    clearInterval(autoSaveIntervalRef.current);
    autoSaveIntervalRef.current = null;
    autoSaveInProgressRef.current = true;
    setSaving(true);

    try {
      const savedId = await onAutoSave({
        id: internalEntryIdRef.current,
        customer_id: customerId,
        session_number: sessionNumber,
        entry_date: formData.session_date,
        sessionData: { ...formData, interventions_used: interventions },
      });
      if (savedId) updateEntryId(savedId);
      setAutoSaveNotification(true);
      clearTimeout(notificationTimerRef.current);
      notificationTimerRef.current = setTimeout(() => setAutoSaveNotification(false), 3000);
    } catch (error) {
      console.error('Failed to save entry:', error);
    } finally {
      autoSaveInProgressRef.current = false;
      setSaving(false);
    }
  };

  const handleClose = () => {
    clearInterval(autoSaveIntervalRef.current);
    autoSaveIntervalRef.current = null;
    clearTimeout(notificationTimerRef.current);
    setAutoSaveNotification(false);
    internalEntryIdRef.current = null;
    autoSaveInProgressRef.current = false;
    setInternalEntryId(null);
    setCustomerId(prefilledCustomerId || '');
    setFormData({
      session_date: new Date().toISOString().split('T')[0],
      duration_minutes: 50,
      mood_assessment: "neutral",
      session_notes: "",
      homework_assigned: ""
    });
    setInterventions([]);
    setNewIntervention("");
    setSessionNumber(1);
    setCustomerSearchTerm('');
    setSelectedCustomerName('');
    onClose();
  };

  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    setShowCustomerDropdown(true);
    handleActivity();

    if (value !== selectedCustomerName) {
      setCustomerId('');
      setSelectedCustomerName('');
    }
  };

  const handleCustomerSelect = (customer) => {
    setCustomerId(customer.id);
    setSelectedCustomerName(customer.full_name);
    setCustomerSearchTerm(customer.full_name);
    setShowCustomerDropdown(false);
  };

  const handleCustomerSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCustomers.length > 0) {
        handleCustomerSelect(filteredCustomers[0]);
      }
    } else if (e.key === 'Escape') {
      setShowCustomerDropdown(false);
    }
  };

  const selectedCustomer = customers.find(c => c.id === customerId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Auto-save notification */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white text-sm font-medium rounded-xl shadow-lg transition-all duration-300 pointer-events-none ${
          autoSaveNotification ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <Save className="w-4 h-4" />
        נשמר אוטומטית
      </div>
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-pink-100 overflow-hidden flex flex-col">        {/* Header */}
        <div className="bg-gradient-to-br from-pink-50 to-purple-50 px-6 py-5 border-b border-pink-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {localViewMode ? 'צפייה ברשומה' : entry ? 'עריכת רשומה' : 'רשומה חדשה'}
                </h2>

                <p className="text-sm text-gray-600">רשומה מוצפנת ביומן הרופא</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-pink-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            {/* Customer Search */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                לקוח <span className="text-red-500">*</span>
              </label>

              <div className="relative" ref={customerDropdownRef}>
                <input
                  ref={customerSearchRef}
                  type="text"
                  value={customerSearchTerm}
                  onChange={handleCustomerSearch}
                  onFocus={() => setShowCustomerDropdown(true)}
                  onKeyDown={handleCustomerSearchKeyDown}
                  placeholder="חפש לפי שם, טלפון או אימייל..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                  disabled={!!prefilledCustomerId || localViewMode}
                  required
                />

                {/* Customer Dropdown */}
                {showCustomerDropdown && filteredCustomers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {filteredCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                        className="px-4 py-3 cursor-pointer hover:bg-pink-50 border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{customer.full_name}</p>
                            <p className="text-sm text-gray-600">{customer.phone}</p>
                          </div>
                          {customer.email && (
                            <p className="text-xs text-gray-500">{customer.email}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* No Results */}
                {showCustomerDropdown && customerSearchTerm.trim() && filteredCustomers.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                    <div className="px-4 py-3 text-gray-500 text-center">
                      לא נמצאו לקוחות
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedCustomer && (
              <div className="p-4 rounded-lg bg-pink-50/50 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      מפגש #{sessionNumber}
                    </p>
                    <p className="text-xs text-gray-600">
                      עם {selectedCustomer.full_name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Session Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                תאריך מפגש <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.session_date}
                disabled={saving || localViewMode}
                onChange={(e) => { setFormData({ ...formData, session_date: e.target.value }); handleActivity(); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                required
              />
            </div>

            {/* Duration & Assessments */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  משך (דקות)
                </label>
                <input
                  type="number"
                  value={formData.duration_minutes}
                  disabled={saving || localViewMode}
                  onChange={(e) => { setFormData({ ...formData, duration_minutes: parseInt(e.target.value) }); handleActivity(); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  מצב רוח
                </label>
                <select
                  value={formData.mood_assessment}
                  onChange={(e) => { setFormData({ ...formData, mood_assessment: e.target.value }); handleActivity(); }}
                  disabled={saving || localViewMode}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  <option value="very_distressed">במצוקה רבה</option>
                  <option value="distressed">במצוקה</option>
                  <option value="neutral">ניטרלי</option>
                  <option value="good">טוב</option>
                  <option value="excellent">מצוין</option>
                </select>
              </div>

            </div>

            {/* Session Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                רשימות מפגש <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.session_notes}
                onChange={(e) => { setFormData({ ...formData, session_notes: e.target.value }); handleActivity(); }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                rows={8}
                placeholder="הערות מפורטות מהמפגש..."
                disabled={saving || localViewMode}
                required
              />
            </div>

            {/* Interventions */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                התערבויות
              </label>
              <div className="flex gap-2">
                <input
                  value={newIntervention}
                  onChange={(e) => { setNewIntervention(e.target.value); handleActivity(); }}
                  placeholder="למשל: ארגון מחשבות, תרגילי נשימה..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addIntervention())}
                  disabled={saving || localViewMode}
                />
                <button
                  type="button"
                  onClick={addIntervention}
                  className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all"
                  disabled={saving || localViewMode}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {interventions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {interventions.map((intervention, index) => (
                    <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-pink-50 text-gray-900 rounded-full text-sm">
                      {intervention}
                      <button
                        type="button"
                        onClick={() => removeIntervention(index)}
                        className="hover:text-red-600"
                        disabled={saving || localViewMode}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Homework */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                משימות בית
              </label>
              <textarea
                value={formData.homework_assigned}
                onChange={(e) => { setFormData({ ...formData, homework_assigned: e.target.value }); handleActivity(); }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                rows={3}
                disabled={saving || localViewMode}
                placeholder="תרגילים או משימות שהוקצו..."
              />
            </div>

            <p className="text-xs text-gray-500 flex items-center gap-1">
              <span>🔒</span>
              <span>כל הנתונים יוצפנו באופן אוטומטי בעת השמירה</span>
            </p>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                disabled={saving}
              >
                סגור
              </button>
              {localViewMode ? (
                <button
                  type="button"
                  onClick={() => setLocalViewMode(false)}
                  className="flex-1 py-3 bg-gradient-to-l from-pink-500 to-purple-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  פתח לעריכה
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 bg-gradient-to-l from-pink-500 to-purple-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'שומר...' : 'שמור רשומה'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};